import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import confirmIcon from '../../../asset/icon/confirm2.png';
import confirmIconHover from '../../../asset/icon/confirm3.png';
import deleteCloseIcon from '../../../asset/icon/delete_close.png';
import deleteOpenIcon from '../../../asset/icon/delete_open.png';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import { getAlertMessage } from '../../../shared/constants/alertMessages';
import { AlertPosition, AlertType } from '../../../shared/enum/EnumRepository';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import { useAdminVenue } from '../../share/hook/useAdminVenue';
import { useAdminVenueTag } from '../../share/hook/useAdminVenueTag';
import { useAdminVenueStore } from '../../share/store/adminVenueStore';
import VenueTagInViewer from './VenueTagInViewer';

const MultiVenueViewer: React.FC = () => {
  const {
    multiSelectedVenueIds,
    multiSelectedVenues,
    multiVenueStatus,
    updateSelectedVenueTags,
    removeSelectedVenues,
  } = useAdminVenue();
  const { tags } = useAdminVenueTag();
  const hasChanged = useAdminVenueStore((state) => state.hasChanged);
  const setHasChanged = useAdminVenueStore((state) => state.setHasChanged);
  const enqueueAlert = useStandardAlertStore((state) => state.enqueueAlert);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const initializedSelectionKeyRef = useRef('');

  const detailsReady = multiVenueStatus === 'success'
    && multiSelectedVenueIds.length > 1
    && multiSelectedVenueIds.every((id) =>
      multiSelectedVenues.some((venue) => String(venue.id) === id)
    );
  const selectionKey = multiSelectedVenueIds.join('|');
  const sourceTagSignature = useMemo(() => multiSelectedVenues
    .map((venue) => [...venue.tags.map((tag) => tag.id)].sort((a, b) => a - b).join(','))
    .join('|'), [multiSelectedVenues]);
  const tagCatalogKey = tags.map((tag) => tag.id).join('|');

  useEffect(() => {
    if (!detailsReady) {
      initializedSelectionKeyRef.current = '';
      setSelectedTagIds([]);
      setHasChanged(false);
      return;
    }
    const countByTagId = new Map<number, number>();
    multiSelectedVenues.forEach((venue) => {
      new Set(venue.tags.map((tag) => tag.id)).forEach((tagId) => {
        countByTagId.set(tagId, (countByTagId.get(tagId) ?? 0) + 1);
      });
    });
    const commonTagIds = [...countByTagId.entries()]
      .filter(([, count]) => count === multiSelectedVenueIds.length)
      .map(([tagId]) => tagId);
    if (initializedSelectionKeyRef.current !== selectionKey || !hasChanged) {
      initializedSelectionKeyRef.current = selectionKey;
      setSelectedTagIds(commonTagIds);
      setHasChanged(false);
      return;
    }

    const validTagIds = new Set(tags.map((tag) => tag.id));
    setSelectedTagIds((current) => current.filter((tagId) => validTagIds.has(tagId)));
  }, [
    detailsReady,
    hasChanged,
    multiSelectedVenueIds.length,
    multiSelectedVenues,
    selectionKey,
    setHasChanged,
    sourceTagSignature,
    tagCatalogKey,
    tags,
  ]);

  useEffect(() => () => {
    useAdminVenueStore.getState().setHasChanged(false);
  }, []);

  if (multiSelectedVenueIds.length < 2) return null;

  const selectedTags = tags.filter((tag) => selectedTagIds.includes(tag.id));
  const handleTagToggle = (tagId: number) => {
    if (!detailsReady) return;
    setSelectedTagIds((current) => current.includes(tagId)
      ? current.filter((id) => id !== tagId)
      : [...current, tagId]);
    setHasChanged(true);
  };

  const handleUpdate = async () => {
    if (!detailsReady || !hasChanged) return;
    await updateSelectedVenueTags(selectedTagIds);
  };

  const handleDelete = () => {
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_DELETE_VENUES_BULK', {
        count: multiSelectedVenueIds.length,
      }),
      callBack: async () => {
        await removeSelectedVenues();
      },
    });
  };

  return (
    <ViewerContainer data-has-changed={hasChanged ? 'true' : 'false'}>
      <ScrollBody>
        <Title>{multiSelectedVenueIds.length} venues selected</Title>
        <FieldBlock>
          <FieldLabel>Label</FieldLabel>
          <TagContainer>
            {detailsReady ? tags.map((tag) => (
              <VenueTagInViewer
                key={tag.id}
                tag={tag}
                selectedTags={selectedTags}
                isEditMode={true}
                onToggle={() => handleTagToggle(tag.id)}
              />
            )) : (
              <StatusText>
                {multiVenueStatus === 'error' ? 'Failed to load venues.' : 'Loading...'}
              </StatusText>
            )}
          </TagContainer>
        </FieldBlock>
      </ScrollBody>
      <ActionBar>
        {hasChanged && detailsReady && (
          <UniversalIconBtn
            isPrimary={true}
            size="fit"
            border={true}
            backgroundImg={confirmIcon}
            backgroundImgHover={confirmIconHover}
            onClick={() => { void handleUpdate(); }}
          />
        )}
        <UniversalIconBtn
          size="fit"
          border={true}
          backgroundImg={deleteCloseIcon}
          backgroundImgHover={deleteOpenIcon}
          onClick={handleDelete}
        />
      </ActionBar>
    </ViewerContainer>
  );
};

const ViewerContainer = styled.section`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ScrollBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 4px 0 56px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Title = styled.h2`
  width: 100%;
  min-height: 28px;
  margin: 0 0 10px;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_02_1};
`;

const FieldBlock = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const FieldLabel = styled.span`
  flex-shrink: 0;
  min-width: 140px;
  padding-top: 2px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-weight: 500;
`;

const TagContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatusText = styled.span`
  color: ${({ theme }) => theme.colors.color_Gray_05};
`;

const ActionBar = styled.div`
  position: absolute;
  right: 0;
  bottom: 0.6vh;
  z-index: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5vh;
`;

export default MultiVenueViewer;
